import { API_ENDPOINTS } from '@/api/endpoints';
import { Department } from '@/types/department';
import { apiFetch } from '@/util/apiClient';
import { getErrorMessage } from '@/util/errorUtils';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface Props {
  open: boolean;
  department?: Department;
  orgId: string;
  onClose: () => void;
}

export default function DepartmentEditModal({
  open,
  department,
  orgId,
  onClose,
}: Props) {
  const [name, setName] = useState(department?.name ?? '');
  const [description, setDescription] = useState(department?.description ?? '');
  const [nameTouched, setNameTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const isEditMode = Boolean(department?.id);

  // Reset form when the department prop changes
  useEffect(() => {
    if (department) {
      setName(department.name);
      setDescription(department.description ?? '');
    } else {
      setName('');
      setDescription('');
    }
    setNameTouched(false);
    setError(null);
  }, [department]);

  // Create department mutation
  const createDepartmentMutation = useMutation({
    mutationFn: () =>
      apiFetch<Department>(API_ENDPOINTS.ORGANIZATIONS.DEPARTMENTS(orgId), {
        method: 'POST',
        body: JSON.stringify({ name, description }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['organization', orgId, 'departments'],
      });
      setError(null);
      onClose();
    },
    onError: (error) => {
      console.error('Failed to create department:', error);
      setError(getErrorMessage(error));
    },
  });

  // Update department mutation
  const updateDepartmentMutation = useMutation({
    mutationFn: () =>
      apiFetch<Department>(
        `${API_ENDPOINTS.ORGANIZATIONS.DEPARTMENTS(orgId)}${department?.id}/`,
        {
          method: 'PATCH',
          body: JSON.stringify({ name, description }),
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['organization', orgId, 'departments'],
      });
      setError(null);
      onClose();
    },
    onError: (error) => {
      console.error('Failed to update department:', error);
      setError(getErrorMessage(error));
    },
  });

  const handleSave = () => {
    if (name.trim()) {
      if (isEditMode) {
        updateDepartmentMutation.mutate();
      } else {
        createDepartmentMutation.mutate();
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={'xs'} fullWidth>
      <DialogTitle>
        {isEditMode ? 'Edit Department' : 'Add Department'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {error && (
            <Alert severity='error' onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <TextField
            label={`Name`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setNameTouched(true)}
            fullWidth
            autoFocus
            required
            error={nameTouched && !name.trim()}
            helperText={
              nameTouched && !name.trim() ? `Department name is required` : ``
            }
            variant={'outlined'}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            label={`Description`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            variant={'outlined'}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={
            !name.trim() ||
            createDepartmentMutation.isPending ||
            updateDepartmentMutation.isPending
          }
          variant={`contained`}
        >
          {isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
